/**
 * analysisExport.js — entregáveis do modo Análise.
 *
 * A partir dos artefatos de análise (markdown), produz:
 *  - Markdown combinado (.md) e por-artefato.
 *  - Um HTML AUTOSSUFICIENTE e NAVEGÁVEL (.html): índice lateral + seções, com os
 *    diagramas mermaid PRÉ-RENDERIZADOS em SVG inline (sem dependência externa).
 *
 * Dependency-light: markdown→HTML é um conversor próprio (XSS-safe por escape);
 * mermaid é a única dep (só p/ pré-renderizar os SVGs, embutidos no arquivo).
 */
import { splitMermaid, sanitizeD2 } from '@/components/MermaidView';

// ── util ──────────────────────────────────────────────────────────────────────
export function downloadFile(name, content, mime = 'text/plain') {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function slugify(s) {
  return String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'secao';
}

function esc(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// inline: `code`, **bold**, *italic*, [txt](url) — sobre texto JÁ escapado.
function inlineMd(text) {
  let t = esc(text);
  t = t.replace(/`([^`]+)`/g, '<code>$1</code>');
  t = t.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  t = t.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  t = t.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" rel="noopener noreferrer">$1</a>');
  return t;
}

/** Conversor Markdown → HTML string (títulos, listas, tabelas, código, citação, hr). */
export function mdToHtml(md) {
  const lines = String(md || '').replace(/\r\n/g, '\n').split('\n');
  const out = [];
  let i = 0;
  const flushList = (buf, ordered) => {
    if (!buf.length) return;
    const tag = ordered ? 'ol' : 'ul';
    out.push(`<${tag}>${buf.map((li) => `<li>${inlineMd(li)}</li>`).join('')}</${tag}>`);
    buf.length = 0;
  };
  while (i < lines.length) {
    const line = lines[i];
    // code fence
    if (/^```/.test(line.trim())) {
      const code = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i].trim())) { code.push(lines[i]); i++; }
      i++;
      out.push(`<pre><code>${esc(code.join('\n'))}</code></pre>`);
      continue;
    }
    // heading
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) { const lv = h[1].length; out.push(`<h${lv}>${inlineMd(h[2])}</h${lv}>`); i++; continue; }
    // hr
    if (/^\s*([-*_])\1{2,}\s*$/.test(line)) { out.push('<hr/>'); i++; continue; }
    // blockquote
    if (/^\s*>\s?/.test(line)) {
      const buf = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) { buf.push(lines[i].replace(/^\s*>\s?/, '')); i++; }
      out.push(`<blockquote>${inlineMd(buf.join(' '))}</blockquote>`);
      continue;
    }
    // table (linha com | e a próxima é separadora ---)
    if (/\|/.test(line) && i + 1 < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1]) && /-/.test(lines[i + 1])) {
      const cells = (r) => r.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map((c) => c.trim());
      const head = cells(line);
      i += 2;
      const rows = [];
      while (i < lines.length && /\|/.test(lines[i]) && lines[i].trim()) { rows.push(cells(lines[i])); i++; }
      const thead = `<thead><tr>${head.map((c) => `<th>${inlineMd(c)}</th>`).join('')}</tr></thead>`;
      const tbody = `<tbody>${rows.map((r) => `<tr>${r.map((c) => `<td>${inlineMd(c)}</td>`).join('')}</tr>`).join('')}</tbody>`;
      out.push(`<table>${thead}${tbody}</table>`);
      continue;
    }
    // unordered list
    if (/^\s*[-*+]\s+/.test(line)) {
      const buf = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) { buf.push(lines[i].replace(/^\s*[-*+]\s+/, '')); i++; }
      flushList(buf, false);
      continue;
    }
    // ordered list
    if (/^\s*\d+[.)]\s+/.test(line)) {
      const buf = [];
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) { buf.push(lines[i].replace(/^\s*\d+[.)]\s+/, '')); i++; }
      flushList(buf, true);
      continue;
    }
    // blank
    if (!line.trim()) { i++; continue; }
    // paragraph (junta linhas até em branco)
    const buf = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,6}\s|```|\s*>|\s*[-*+]\s|\s*\d+[.)]\s)/.test(lines[i])) {
      buf.push(lines[i]); i++;
    }
    out.push(`<p>${inlineMd(buf.join(' '))}</p>`);
  }
  return out.join('\n');
}

// carrega mermaid sob demanda (dep pesada) e configura igual ao MermaidView.
// Mantido para renderizar diagramas ```mermaid``` LEGADOS em relatórios antigos.
let _mermaidP = null;
function loadMermaid() {
  if (!_mermaidP) {
    _mermaidP = import('mermaid').then((mod) => {
      const m = mod.default ?? mod;
      m.initialize({ startOnLoad: false, theme: 'dark', securityLevel: 'strict' });
      return m;
    });
  }
  return _mermaidP;
}

// carrega o D2 (Terrastruct) sob demanda — motor padrão de diagramas (```d2```).
let _d2P = null;
function loadD2() {
  if (!_d2P) {
    _d2P = import('@terrastruct/d2').then((mod) => {
      const D2 = mod.D2 ?? mod.default?.D2 ?? mod.default;
      return new D2();
    });
  }
  return _d2P;
}

/** Renderiza um artefato (markdown + diagramas d2/mermaid) para HTML, com SVGs inline. */
async function artifactToHtml(content, keyPrefix) {
  const parts = splitMermaid(content || '');
  const chunks = [];
  let mermaid = null;
  let d2 = null;
  for (let idx = 0; idx < parts.length; idx++) {
    const p = parts[idx];
    // splitMermaid expõe o texto de cada parte em `p.value` (não `p.content`) —
    // usar o campo errado deixava TODO o HTML exportado vazio (texto→'' e diagrama
    // →fallback "undefined"), embora os accordions on-screen (RichContent) funcionem.
    const raw = p.value ?? p.content ?? '';
    if (p.type === 'd2') {
      try {
        if (!d2) d2 = await loadD2();
        const salt = `${keyPrefix}-d${idx}`;
        const opts = { layout: 'dagre', themeID: 200, pad: 12, center: true, noXMLTag: true, salt };
        let svg;
        try {
          const r = await d2.compile(raw, opts);
          svg = await d2.render(r.diagram, { ...(r.renderOptions || {}), themeID: 200, noXMLTag: true, salt });
        } catch {
          // 2ª tentativa: sanitizeD2 (escapa `$`, cita rótulos com char especial).
          const s = `${salt}s`;
          const r2 = await d2.compile(sanitizeD2(raw), { ...opts, salt: s });
          svg = await d2.render(r2.diagram, { ...(r2.renderOptions || {}), themeID: 200, noXMLTag: true, salt: s });
        }
        chunks.push(`<div class="mermaid-svg">${svg}</div>`);
      } catch {
        chunks.push(`<pre class="mermaid-fallback"><code>${esc(raw)}</code></pre>`);
      }
    } else if (p.type === 'mermaid') {
      try {
        if (!mermaid) mermaid = await loadMermaid();
        const { svg } = await mermaid.render(`${keyPrefix}-m${idx}`, raw);
        chunks.push(`<div class="mermaid-svg">${svg}</div>`);
      } catch {
        chunks.push(`<pre class="mermaid-fallback"><code>${esc(raw)}</code></pre>`);
      }
    } else {
      chunks.push(mdToHtml(raw));
    }
  }
  return chunks.join('\n');
}

/** Markdown combinado (todos os artefatos, em ordem). */
export function artifactsToMarkdown(artifacts, projectName = 'Análise') {
  const head = `# ${projectName} — Relatório de Análise\n\n_Gerado pelo Nava Sphere._\n`;
  const body = (artifacts || [])
    .map((a) => `\n\n---\n\n# ${a.title || a.kind}\n\n${a.content || ''}`)
    .join('');
  return head + body + '\n';
}

/** HTML autossuficiente e navegável (índice + seções + mermaid inline). */
export async function buildAnalysisHtml(artifacts, projectName = 'Análise') {
  const list = artifacts || [];
  const sections = [];
  for (let k = 0; k < list.length; k++) {
    const a = list[k];
    const id = `sec-${k}-${slugify(a.title || a.kind)}`;
    const html = await artifactToHtml(a.content, `art${k}`);
    sections.push({ id, title: a.title || a.kind, html });
  }
  const nav = sections.map((s) => `<li><a href="#${s.id}">${esc(s.title)}</a></li>`).join('');
  const body = sections.map((s) =>
    `<section id="${s.id}"><h1 class="sec-title">${esc(s.title)}</h1>${s.html}</section>`).join('\n');

  return `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>${esc(projectName)} — Análise</title>
<style>
  :root { color-scheme: dark; }
  * { box-sizing: border-box; }
  body { margin:0; background:#0C0C0C; color:#F8F8F0; font:15px/1.65 -apple-system,Segoe UI,Roboto,sans-serif; }
  a { color:#01E6E4; text-decoration:none; } a:hover { text-decoration:underline; }
  .wrap { display:flex; min-height:100vh; }
  nav.toc { width:280px; flex-shrink:0; position:sticky; top:0; align-self:flex-start; height:100vh;
    overflow:auto; border-right:1px solid #2A2A2A; padding:22px 18px; background:#0F0F0F; }
  nav.toc .brand { font-weight:800; color:#01E6E4; letter-spacing:-0.02em; margin-bottom:4px; }
  nav.toc .sub { color:#6B6B66; font-size:12px; margin-bottom:16px; }
  nav.toc ul { list-style:none; margin:0; padding:0; }
  nav.toc li { margin:2px 0; } nav.toc a { display:block; padding:7px 10px; border-radius:7px; color:#A8A8A0; }
  nav.toc a:hover { background:#1F1F1F; color:#F8F8F0; text-decoration:none; }
  main { flex:1; min-width:0; padding:32px 40px 80px; max-width:1000px; }
  section { border-bottom:1px solid #1F1F1F; padding-bottom:28px; margin-bottom:28px; }
  .sec-title { font-size:24px; letter-spacing:-0.02em; border-left:3px solid #01E6E4; padding-left:12px; }
  h1,h2,h3,h4 { color:#F8F8F0; letter-spacing:-0.01em; } h2 { font-size:19px; margin-top:26px; }
  h3 { font-size:16px; color:#A8A8A0; } code { background:#1F1F1F; padding:1px 6px; border-radius:5px;
    font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-size:0.9em; color:#01E6E4; }
  pre { background:#08090A; border:1px solid #2A2A2A; border-radius:8px; padding:14px; overflow:auto; }
  pre code { background:none; color:#A8A8A0; padding:0; }
  blockquote { border-left:3px solid #5C00E0; margin:12px 0; padding:4px 14px; color:#A8A8A0; background:#141414; }
  table { border-collapse:collapse; width:100%; margin:14px 0; overflow:auto; display:block; }
  th,td { border:1px solid #2A2A2A; padding:8px 12px; text-align:left; font-size:13.5px; }
  th { background:#1F1F1F; color:#F8F8F0; } td { color:#A8A8A0; }
  hr { border:none; border-top:1px solid #2A2A2A; margin:22px 0; }
  .mermaid-svg { background:#0F0F0F; border:1px solid #2A2A2A; border-radius:10px; padding:14px; margin:16px 0; overflow:auto; }
  .mermaid-svg svg { max-width:100%; height:auto; }
  @media (max-width:760px){ nav.toc{ display:none; } main{ padding:20px; } }
</style></head>
<body><div class="wrap">
  <nav class="toc"><div class="brand">nava · Análise</div>
    <div class="sub">${esc(projectName)}</div><ul>${nav}</ul></nav>
  <main>${body}</main>
</div></body></html>`;
}
